import { c as _c } from "react/compiler-runtime";
import { Provider } from '@ai-sdk-tools/store';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import type { Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { UIMessageWithMetadata } from '../../../lib/types';
export { NovaProvider, useNovaWrapperState, type NovaProviderProps, type NovaProviderCallbacks } from './nova-provider';
interface NovaContextValue {
  userUuid: string;
  accountUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint: string;
  baseUrl?: string;
}
export type { NovaContextValue };
const NovaContext = createContext<NovaContextValue | undefined>(undefined);
interface NovaChatProviderProps {
  children: ReactNode;
  userUuid: string;
  accountUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint?: string;
  baseUrl?: string;
  initialMessages?: UIMessageWithMetadata[];
}

/**
 * Combined provider that wraps both ChatProvider (from @ai-sdk-tools/store)
 * and NovaContextProvider. This is the main entry point for the chat system.
 *
 * Simplified to merge both providers into one to reduce nesting.
 *
 * @example
 * ```tsx
 * <NovaChatProvider
 *   authProvider={authProvider}
 *   userUuid={userUuid}
 *   accountUuid={accountUuid}
 * >
 *   <ChatRoot chatUuid={chatUuid}>
 *     <Messages />
 *     <ChatInput />
 *   </ChatRoot>
 * </NovaChatProvider>
 * ```
 */
export function NovaChatProvider(t0) {
  const $ = _c(14);
  const {
    children,
    userUuid,
    accountUuid,
    accountName,
    scopes,
    authProvider,
    apiEndpoint: t1,
    baseUrl,
    initialMessages
  } = t0;
  const apiEndpoint = t1 === undefined ? "/api/ai/nova/v1/chat/completions" : t1;
  let t2;
  let t3;
  if ($[0] !== accountName || $[1] !== accountUuid || $[2] !== apiEndpoint || $[3] !== authProvider || $[4] !== baseUrl || $[5] !== scopes || $[6] !== userUuid) {
    t3 = {
      userUuid,
      accountUuid,
      accountName,
      scopes,
      authProvider,
      apiEndpoint,
      baseUrl
    };
    $[0] = accountName;
    $[1] = accountUuid;
    $[2] = apiEndpoint;
    $[3] = authProvider;
    $[4] = baseUrl;
    $[5] = scopes;
    $[6] = userUuid;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  t2 = t3;
  const novaContextValue = t2;
  let t4;
  if ($[8] !== children || $[9] !== novaContextValue) {
    t4 = <NovaContext.Provider value={novaContextValue}>{children}</NovaContext.Provider>;
    $[8] = children;
    $[9] = novaContextValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== initialMessages || $[12] !== t4) {
    t5 = <Provider initialMessages={initialMessages}>{t4}</Provider>;
    $[11] = initialMessages;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

/**
 * Hook to access Nova context values.
 * @throws Error if used outside of NovaChatProvider
 */
export function useNovaContext() {
  const context = useContext(NovaContext);
  if (!context) {
    throw new Error("useNovaContext must be used within NovaChatProvider");
  }
  return context;
}
export type { NovaChatProviderProps };